import styled from "styled-components/macro";

import FnrContainedIcon from "UI/FnrContainedIcon";
import { colors } from "UI/colors";

import ChipLabel from "./ChipLabel";
import { chipBox, chipChrome } from "./chipPillStyle";

/**
 * The canonical "USPS Verified" chip — a light-blue pill with the USPS eagle
 * and the label "USPS Verified", in the same blues as the standardization
 * notice card ([`StandardizationNotice`](./StandardizationNotice.jsx)): fnBlue100
 * background, fnBlue200 border, fnBlue icon, fnBlue800 text. The label matches
 * the profile field-name size (12px). This is the single source of truth for the badge
 * visual; both the admin profile badge
 * ([`VerificationBadge`](../../ExtendedFieldInputs/InputSwitch/AddressInput/VerificationBadge.jsx)'s
 * `usps` state) and the autocomplete surfaces ([`UspsVerifiedBadge`](./UspsVerifiedBadge.jsx))
 * render this so the mark is defined in exactly one place.
 *
 * It is purely presentational — no toggle, no interaction. Each consumer wraps
 * it in its own `FnrOpacityToggle` with its own reveal trigger: the autocomplete
 * surfaces keep it mounted and fade it on `verified` (via `UspsVerifiedBadge`'s
 * `show`); the admin profile reveals on hover/focus.
 *
 * Trademark scope: the glyph is the USPS "sonic eagle" mark (FontAwesome brands
 * `fa-usps fab`), a registered USPS trademark. The business approved showing it
 * to identify the genuine source of verification (a nominative use). That
 * approval was originally scoped to the admin read-only view; it has since been
 * intentionally **broadened** to the autocomplete surfaces (sacrament forms, the
 * family-registration public page, and signups), where the badge renders only
 * when the address was confirmed as a Smarty leaf (`isVerified`). The condition
 * that keeps the use legitimate is unchanged: render this pill ONLY for an
 * address that was actually verified — never for an admin-marked or unverified
 * address. Keep every caller gated accordingly (`VerificationBadge`'s
 * `getAddressBadgeState` on the USPS provider; the autocomplete surfaces on the
 * hook's `verified`).
 */
export default function UspsVerifiedPill() {
  return (
    <Pill aria-label="address-verified-usps">
      {/* `noPadding` strips the container's built-in slack so the icon→label
          spacing comes from one place — chipChrome's `gap` — and matches the
          tighter chip family. `noAnimation` keeps the eagle static (this is a
          read-only badge, not a button). */}
      <FnrContainedIcon
        icon="fa-usps fab"
        size="lg"
        noBackgroundColor
        color={colors.fnBlue}
        noPadding
        noAnimation
      />
      <ChipLabel>USPS Verified</ChipLabel>
    </Pill>
  );
}

// Chrome + box from the shared chip fragments (single source of truth for the
// family's dimensions). The label typography lives in ChipLabel. font-size (in
// chipChrome) is the em base FnrContainedIcon scales from: lg = 1.125em of 12px
// ≈ 13.5px eagle; without it the icon grows to the 16px base.
const Pill = styled.span`
  ${chipChrome}
  ${chipBox}
`;
